import React, { type ComponentType } from 'react';
import type { ExecutionResult, MutationFunctionOptions } from 'react-apollo';
import type { MutationUpdaterFn } from 'apollo-client';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { doc, mention, p, text } from '@atlaskit/adf-utils/builders';

import type { MutationFunctionInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { type AddCommentInCacheProps } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { addEmptyObjectRepliesArray } from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import {
	type useCreateObjectCommentMutation$data,
	type useCreateObjectCommentMutation$variables,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutationTypes.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/comments-data-manager/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import {
	handleInlineAnnotationNavigation,
	handleFooterCommentsNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { scrollCommentIntoView } from '@confluence/comments-util/entry-points/domUtils.ts';
import {
	scrollToElementInEditor,
	checkElementExpandInEditor,
} from '@confluence/comments-util/entry-points/editorAnnotations.ts';
import {
	handleMutationFailure,
	handleCreateReplySuccess as handleCreateInlineReplySuccess,
	updateApolloCacheCallback as updateInlineApolloCacheCallback,
	getCommentCreationLocation,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { CommentLocation } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { GraphQLContentStatus } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import {
	updateApolloCacheReplyCallback as updateApolloCacheGeneralReplyCallback,
	handleCreateReplySuccess as handleCreateGeneralReplySuccess,
	type UpdateApolloCacheReplyCallbackDataType,
} from '@confluence/page-comments-queries/entry-points/pageCommentUtils.ts';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import type { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import type { ReplyToCommentUser } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralReplyMutation.ts';
import type { CreateGeneralReplyMutationVariables } from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralReplyMutation.ts';
import type { ContentTypesCommentService } from '@confluence/content-types-bridge-comments';

import type {
	CommentRepliesCreateGeneralReplyMutation as CommentRepliesCreateGeneralReplyMutation$data,
	CommentRepliesCreateGeneralReplyMutationVariables as CommentRepliesCreateGeneralReplyMutation$variables,
} from '../components/__types__/CommentRepliesCreateGeneralReplyMutation.ts';
import type { CommentThreadProps } from '../components/CommentThread.tsx';
import type {
	CommentRepliesCreateInlineReplyMutation as CommentRepliesCreateInlineReplyMutation$data,
	CommentRepliesCreateInlineReplyMutationVariables as CommentRepliesCreateInlineReplyMutation$variables,
} from '../components/__types__/CommentRepliesCreateInlineReplyMutation.ts';

export const DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT = 0;
export const LEGACY_NUM_VISIBLE_REPLIES = 2;
export const DEFAULT_NUM_VISIBLE_REPLIES = 3;
export const STANDALONE_SHOW_ALL_REPLIES_CUTOFF = 5;
export const COMMENTS_PANEL_SHOW_ALL_REPLIES_CUTOFF = 4;

type HandleCommentThreadSelectionProps = {
	parentComment: CommentData;
	pageMode: PageMode;
	setCurrentlySelectedThreadKey: (
		parentCommentMarkerRef: string | undefined,
		commentType?: CommentType,
	) => void;
	scrollCommentsPanelToThread: ({
		annotationId,
		isCommentThreadResolved,
		commentType,
	}: {
		annotationId: string;
		isCommentThreadResolved?: boolean;
		commentType?: CommentType;
	}) => void;
	eventEmitter?: AnnotationUpdateEmitter;
	event?: React.MouseEvent;
	handleSelectThread?: (threadId?: string) => void;
};

type HandlePanelSelectionAndScrollProps = {
	threadKey: string | null;
	isResolved: boolean;
	pageMode: PageMode;
	setCurrentlySelectedThreadKey: (
		parentCommentMarkerRef: string | undefined,
		commentType?: CommentType,
	) => void;
	eventEmitter?: AnnotationUpdateEmitter;
	commentType: CommentType;
	scrollCommentsPanelToThread?: ({
		annotationId,
		commentType,
		hasKeyPressed,
	}: {
		annotationId: string;
		commentType?: CommentType;
		hasKeyPressed?: boolean;
	}) => void;
	shouldScrollCommentsPanel?: boolean;
	event?: React.MouseEvent;
	id?: string;
	hasKeyPressed?: boolean;
};

const shouldTriggerCommentScroll = (event: React.MouseEvent, id?: string): boolean => {
	if (!id) {
		return false;
	}
	const target = event.target as HTMLElement;
	const isContainer = target === event.currentTarget;
	const isCommentBody = Boolean(target.closest(`[data-testid="comment-thread-${id}-parent"]`));
	const isCommentReplyBody = Boolean(
		target.closest(`[data-testid="comment-thread-${id}-reply-list"]`),
	);
	const replyToggler = Boolean(target.closest('[data-testid="replies-toggler-container"]'));

	// Don't trigger if clicking on specific interactive elements
	const isInteractiveElement = Boolean(
		target.closest('button, a, input, textarea, [role="button"]'),
	);
	const isEmojiElement = Boolean(
		target.closest('[data-testid="emoji-add-icon"], [data-emoji-short-name]'),
	);

	const isGeneralReplyScroller = Boolean(
		target.closest('[data-testid="comments-panel-reply-scroller-container"]'),
	);
	const isFooterCommentThreadLoaded = Boolean(
		document.getElementById(`footer-comment-thread-${id}-container`),
	);

	// NOTE - handles scrolling in place of the general reply scroller when the footer comment thread is not yet in the DOM
	// This is a temporary solution until we update our data loading patterns. Once they're updated, this will always be false and can be removed.
	const scrollInPlaceOfGeneralReplyScroller =
		isGeneralReplyScroller &&
		!isFooterCommentThreadLoaded &&
		fg('confluence_frontend_content_wrapper');

	return (
		(isContainer ||
			isCommentBody ||
			isCommentReplyBody ||
			replyToggler ||
			isGeneralReplyScroller) &&
		(!isInteractiveElement || scrollInPlaceOfGeneralReplyScroller) &&
		!isEmojiElement
	);
};

// Standalone collapses by default at STANDALONE_SHOW_ALL_REPLIES_CUTOFF, and then shows DEFAULT_NUM_VISIBLE_REPLIES after
// The panel displays DEFAULT_NUM_VISIBLE_REPLIES for all views, except for RESOLVED and DELETED_CONTENT views,
// where it displays DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT.
export const getDefaultNumVisibleReplies = (
	numberOfReplies: number,
	isStandaloneComment: boolean = false,
	isParentCommentClosedOrDangling: boolean = false,
) => {
	if (isParentCommentClosedOrDangling && fg('confluence_frontend_new_dangling_comments_ux')) {
		return DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT;
	}

	if (isStandaloneComment && numberOfReplies <= STANDALONE_SHOW_ALL_REPLIES_CUTOFF) {
		return STANDALONE_SHOW_ALL_REPLIES_CUTOFF;
	}

	if (expValEquals('confluence_comments_tuneup', 'isEnabled', true)) {
		return numberOfReplies >= COMMENTS_PANEL_SHOW_ALL_REPLIES_CUTOFF
			? LEGACY_NUM_VISIBLE_REPLIES
			: DEFAULT_NUM_VISIBLE_REPLIES;
	}

	return LEGACY_NUM_VISIBLE_REPLIES;
};

export const getExperienceName = (isInlineComment: boolean) =>
	isInlineComment
		? REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE
		: REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE;

export const handleCommentThreadSelection = ({
	parentComment,
	pageMode,
	setCurrentlySelectedThreadKey,
	scrollCommentsPanelToThread,
	eventEmitter,
	event,
	handleSelectThread,
}: HandleCommentThreadSelectionProps) => {
	const threadKey =
		parentComment.type === CommentType.INLINE
			? (parentComment.location as CommentLocation).inlineMarkerRef
			: parentComment.id;
	const isResolved = !parentComment.isOpen;

	// Send a message via bridge api if applicable
	if (threadKey && handleSelectThread) {
		handleSelectThread(threadKey);
	}

	handlePanelSelectionAndScroll({
		threadKey,
		isResolved,
		pageMode,
		setCurrentlySelectedThreadKey,
		eventEmitter,
		commentType: parentComment.type,
		scrollCommentsPanelToThread,
		shouldScrollCommentsPanel: true,
		event,
		id: parentComment.id,
	});
};

export const handlePanelSelectionAndScroll = ({
	threadKey,
	isResolved,
	pageMode,
	setCurrentlySelectedThreadKey,
	eventEmitter,
	commentType,
	scrollCommentsPanelToThread = undefined,
	shouldScrollCommentsPanel = false,
	event,
	id,
	hasKeyPressed,
}: HandlePanelSelectionAndScrollProps) => {
	// We only scroll the document for non-resolved inline comments or page comments
	if (threadKey && !isResolved) {
		if (event && !shouldTriggerCommentScroll(event, id)) {
			setCurrentlySelectedThreadKey(threadKey);
		} else {
			setCurrentlySelectedThreadKey(threadKey, commentType);
		}
		if (commentType === CommentType.INLINE) {
			const commentElement = document.getElementById(threadKey);
			if (commentElement) {
				if (fg('confluence_comments_unified_navigation')) {
					// Determine navigation context based on page mode
					const navigationContext =
						pageMode === PageMode.EDIT || pageMode === PageMode.LIVE
							? NavigationContext.EDITOR
							: NavigationContext.RENDERER;

					handleInlineAnnotationNavigation({
						scrollBehavior: ScrollBehavior.SMOOTH,
						context: navigationContext,
						block: 'center',
						inline: 'nearest',
						markElement: commentElement,
						annotationId: threadKey,
					});
				} else {
					if (pageMode === PageMode.EDIT || pageMode === PageMode.LIVE) {
						((event && shouldTriggerCommentScroll(event, id)) || hasKeyPressed) &&
							checkElementExpandInEditor(commentElement);
						scrollToElementInEditor({ targetElement: commentElement, viewingRoomOffset: -300 });
						eventEmitter?.emit('setselectedannotation', threadKey);
					} else {
						eventEmitter?.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
							annotationId: threadKey,
						});
						if ((event && shouldTriggerCommentScroll(event, id)) || hasKeyPressed) {
							scrollCommentIntoView({
								commentElement,
								isFabricPage: true,
								useInstantScroll: false,
								commentThreadAnnotationId: threadKey,
								isCommentsPanel: true,
								scrollCommentsPanelToThread,
								shouldScrollCommentsPanel,
							});
						}
					}
				}
			}
		} else if (commentType === CommentType.GENERAL) {
			handleFooterCommentsNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				inline: 'nearest',
				commentId: threadKey,
			});
		}

		// Handle case where a comment is never clicked but is still selected via KB shortcuts
		if (shouldScrollCommentsPanel && scrollCommentsPanelToThread) {
			if (fg('confluence_frontend_content_wrapper')) {
				if (!fg('confluence_comments_unified_navigation')) {
					scrollCommentsPanelToThread({ annotationId: threadKey, commentType, hasKeyPressed });
				}
			}
		}
	}
};

export type SaveProps = {
	pageId: string;
	parentCommentId: string;
	cloudId: string;
	adf: object;
	onSuccess: () => void;
	pageMode: PageMode;
	editCommentQueryId?: string;
	setCommentForEdit: React.Dispatch<React.SetStateAction<string>>;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	pageType: string;
	experienceTracker: ExperienceTrackerAPI;
	createInlineReplyFn?: (
		options:
			| MutationFunctionOptions<
					CommentRepliesCreateInlineReplyMutation$data,
					CommentRepliesCreateInlineReplyMutation$variables
			  >
			| undefined,
		onCacheUpdateError: (error: Error) => void,
	) => Promise<ExecutionResult<CommentRepliesCreateInlineReplyMutation$data>>;
	// TODO: Remove this prop when the new Comments Data Manager is fully adopted
	createGeneralReplyFn?: (
		options?:
			| MutationFunctionOptions<
					UpdateApolloCacheReplyCallbackDataType,
					CommentRepliesCreateGeneralReplyMutation$variables
			  >
			| undefined,
	) => Promise<ExecutionResult<CommentRepliesCreateGeneralReplyMutation$data>>;
	createObjectReplyFn?: MutationFunctionInterop<
		useCreateObjectCommentMutation$data,
		useCreateObjectCommentMutation$variables
	>;
	addCommentInCommentsDataManagerCacheFn?: (props: AddCommentInCacheProps) => void;
	createFooterReplyFn?: (
		options?:
			| MutationFunctionOptions<
					UpdateApolloCacheReplyCallbackDataType,
					CreateGeneralReplyMutationVariables
			  >
			| undefined,
	) => Promise<ExecutionResult<CommentRepliesCreateGeneralReplyMutation$data>>;
	selectedAnnotation?: AnnotationInfo;
	addReplyToCommentThread: (ref: string, reply: ReplyData, commentType: CommentType) => void;
	threadKey: string;
	updateReadCommentsListState: (
		transform: (prevReadCommentsListState: Set<string>) => Set<string>,
	) => void;
	commentType: CommentType;
	searchSessionId?: string | null;
	isReactionsEnabled?: boolean;
	analyticsSource: AnalyticsSource;
	contentTypesCommentService?: ContentTypesCommentService;
	isFooterComment?: boolean;
	setReplyToCommentId: (
		args_0?:
			| { parentCommentId?: string; location?: LocationValue; user?: ReplyToCommentUser }
			| undefined,
	) => void;
	resetContentChanged: () => void;
	setReplyView: (view: ViewReplyOptions) => void;
};

// Build mutation options for creating replies, split by reply kind to keep the main flow concise
const buildInlineReplyOptions = ({
	pageId,
	parentCommentId,
	adf,
	isEditor,
	pageMode,
	threadKey,
	includeReactionsFlag,
}: {
	pageId: string;
	parentCommentId: string;
	adf: object;
	isEditor: boolean;
	pageMode: PageMode;
	threadKey: string;
	includeReactionsFlag: boolean;
}) => ({
	variables: {
		input: {
			containerId: pageId,
			parentCommentId,
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			createdFrom: getCommentCreationLocation(isEditor, pageMode === PageMode.LIVE),
		},
		pageId,
		includeReactions: includeReactionsFlag,
	},
	update: updateInlineApolloCacheCallback(
		'create',
		{
			pageId,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
			annotationId: threadKey,
		},
		parentCommentId,
	) as MutationUpdaterFn<CommentRepliesCreateInlineReplyMutation$data>,
});

const buildGeneralReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	includeReactionsFlag,
	isReactionsEnabled,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	includeReactionsFlag: boolean;
	isReactionsEnabled?: boolean;
}) => ({
	variables: {
		pageId,
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			commentSource: Platform.WEB,
			containerId: pageId,
			parentCommentId,
		},
		includeReactions: includeReactionsFlag,
	},
	update: updateApolloCacheGeneralReplyCallback(
		'create',
		{
			pageId,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
			type: ['FOOTER'],
		},
		parentCommentId,
		isReactionsEnabled,
	) as MutationUpdaterFn<UpdateApolloCacheReplyCallbackDataType>,
});

export const buildObjectReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	addCommentInCommentsDataManagerCacheFn,
	experienceTracker,
	isInlineComment,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	addCommentInCommentsDataManagerCacheFn?: (props: AddCommentInCacheProps) => void;
	experienceTracker: ExperienceTrackerAPI;
	isInlineComment: boolean;
}) => ({
	variables: {
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			commentSource: Platform.WEB,
			containerId: pageId,
			parentCommentId,
		},
		pageId,
	},
	update: (...params: Parameters<MutationUpdaterFn<useCreateObjectCommentMutation$data>>) => {
		const [_dataProxy, mutationResult] = params;
		addCommentInCommentsDataManagerCacheFn?.({
			newComment: addEmptyObjectRepliesArray(mutationResult.data!.createFooterComment),
			onError: (error: Error) => {
				void handleMutationFailure({
					experienceTracker,
					experienceName: getExperienceName(isInlineComment),
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			},
		});
	},
});

const buildFooterReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	isReactionsEnabled,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	isReactionsEnabled?: boolean;
}) => ({
	variables: {
		pageId,
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
			},
			commentSource: Platform.WEB,
			containerId: pageId,
			parentCommentId,
		},
	},
	update: updateApolloCacheGeneralReplyCallback(
		'create',
		{
			pageId,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
			type: ['FOOTER'],
		},
		parentCommentId,
		isReactionsEnabled,
	) as MutationUpdaterFn<UpdateApolloCacheReplyCallbackDataType>,
});

export const onSaveReply = async ({
	pageId,
	parentCommentId,
	cloudId,
	adf,
	onSuccess,
	pageMode,
	editCommentQueryId,
	setCommentForEdit,
	createAnalyticsEvent,
	pageType,
	experienceTracker,
	createInlineReplyFn,
	createGeneralReplyFn,
	createObjectReplyFn, // Create reply via Comments Data Manager
	addCommentInCommentsDataManagerCacheFn, // Add comment to Comments Data Manager cache, passed in because it comes from a hook
	createFooterReplyFn,
	selectedAnnotation,
	addReplyToCommentThread,
	threadKey,
	updateReadCommentsListState,
	commentType,
	searchSessionId,
	isReactionsEnabled,
	analyticsSource,
	contentTypesCommentService,
	isFooterComment,
	setReplyToCommentId,
	resetContentChanged,
	setReplyView,
}: SaveProps) => {
	const isInlineComment = commentType === CommentType.INLINE;
	const isEditor = pageMode === PageMode.EDIT;

	let startExperienceName = REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE;

	if (!isInlineComment) {
		startExperienceName = REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE;
	}

	experienceTracker.start({
		name: startExperienceName,
		timeout: ExperienceTimeout.EDITOR_PUBLISH,
		attributes: getCommentsDataManagerExperimentAttributes(),
	});

	let createReplyPromise = undefined;
	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		// New path - improved logic + Comments Data Manager support
		if (isInlineComment) {
			createReplyPromise = createInlineReplyFn?.(
				buildInlineReplyOptions({
					pageId,
					parentCommentId,
					adf,
					isEditor,
					pageMode,
					threadKey,
					includeReactionsFlag: false,
				}),
				(error: Error) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName: getExperienceName(isInlineComment),
						error,
						commentId: parentCommentId,
						shouldReturnError: true,
					});
				},
			);
		} else if (createObjectReplyFn) {
			// The createObjectReplyFn takes precedence over other non-inline (footer or general) reply
			// functions, if it was provided
			createReplyPromise = createObjectReplyFn(
				buildObjectReplyOptions({
					pageId,
					cloudId,
					parentCommentId,
					adf,
					addCommentInCommentsDataManagerCacheFn,
					experienceTracker,
					isInlineComment,
				}),
			);
		} else if (isFooterComment) {
			createReplyPromise = createFooterReplyFn?.(
				buildFooterReplyOptions({
					pageId,
					cloudId,
					parentCommentId,
					adf,
					isReactionsEnabled,
				}),
			);
		} else {
			createReplyPromise = createGeneralReplyFn?.(
				buildGeneralReplyOptions({
					pageId,
					cloudId,
					parentCommentId,
					adf,
					includeReactionsFlag: false,
					isReactionsEnabled,
				}),
			);
		}
	} else {
		// Legacy path - use old logic
		createReplyPromise = isInlineComment
			? createInlineReplyFn &&
				createInlineReplyFn(
					buildInlineReplyOptions({
						pageId,
						parentCommentId,
						adf,
						isEditor,
						pageMode,
						threadKey,
						includeReactionsFlag: false,
					}),
					() => {},
				)
			: isFooterComment
				? createFooterReplyFn &&
					createFooterReplyFn(
						buildFooterReplyOptions({
							pageId,
							cloudId,
							parentCommentId,
							adf,
							isReactionsEnabled,
						}),
					)
				: createGeneralReplyFn &&
					createGeneralReplyFn(
						buildGeneralReplyOptions({
							pageId,
							cloudId,
							parentCommentId,
							adf,
							includeReactionsFlag: false,
							isReactionsEnabled,
						}),
					);
	}

	return (
		createReplyPromise &&
		createReplyPromise
			.then(({ data }: any) => {
				const replyResult = isInlineComment ? data.replyInlineComment : data.createFooterComment;
				const reply: ReplyData = {
					isUnread: false,
					type: isInlineComment ? CommentType.INLINE : CommentType.GENERAL,
					...replyResult,
				};
				updateReadCommentsListState((prev) => new Set([...prev, reply.id]));
				addReplyToCommentThread(threadKey, reply, commentType);

				// Update content types comments if we are creating a reply on a different content type
				contentTypesCommentService?.onUpdateComment?.(threadKey);

				// Reset reply state after successful creation
				setReplyToCommentId({ parentCommentId: undefined });
				resetContentChanged();
				setReplyView(ViewReplyOptions.ALL);

				if (isInlineComment) {
					handleCreateInlineReplySuccess({
						onSuccess,
						editCommentQueryId,
						setCommentForEdit,
						data,
						createAnalyticsEvent,
						pageId,
						pageType,
						analyticsSource,
						commentId: parentCommentId,
						pageMode,
						selectedAnnotation,
						getInlineNodeTypes: undefined,
						experienceTracker,
						isEditor,
					});
				} else {
					handleCreateGeneralReplySuccess({
						onSuccess,
						editCommentQueryId,
						setCommentForEdit,
						data,
						createAnalyticsEvent,
						pageId,
						pageType,
						analyticsSource,
						commentId: parentCommentId,
						pageMode,
						experienceTracker,
						searchSessionId,
						isFooterComment,
					});
				}
			})
			.catch((error: any) => {
				let failExperienceName;
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					failExperienceName = getExperienceName(isInlineComment);
				} else {
					failExperienceName = REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE;

					if (!isInlineComment) {
						failExperienceName = REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE;
					}
				}

				return handleMutationFailure({
					experienceTracker,
					experienceName: failExperienceName,
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			})
	);
};

export const getCommentThreadsToBatch: (
	comments: CommentData[],
	passThroughProps: any,
	CommentThreadComponent: ComponentType<CommentThreadProps>,
	transitionIndexWithinBatch: number | undefined,
) => React.JSX.Element[] = (
	comments,
	passThroughProps,
	CommentThreadComponent,
	transitionIndexWithinBatch,
) =>
	comments.map((parentComment, index) => {
		const shouldRenderCommentTypeSeparator = index === transitionIndexWithinBatch;

		return (
			<CommentThreadComponent
				key={parentComment.id}
				parentComment={parentComment}
				shouldRenderCommentTypeSeparator={shouldRenderCommentTypeSeparator}
				{...passThroughProps}
			/>
		);
	});

export const hasUnreadHiddenReply = (reply: ReplyData, hiddenReplies: ReplyData[]) => {
	return hiddenReplies.some(
		(hiddenReply: ReplyData) => hiddenReply.id === reply.id && hiddenReply.isUnread,
	);
};

export const getDefaultReplyView = (
	numberOfReplies: number,
	isParentCommentResolvedOrDangling: boolean,
	isStandaloneComment: boolean = false,
) => {
	if (!fg('confluence_frontend_cp_visible_replies_refactor')) return ViewReplyOptions.DEFAULT;

	if (isParentCommentResolvedOrDangling) {
		return ViewReplyOptions.HIDDEN;
	}

	if (
		(isStandaloneComment && fg('confluence_comment_expand_all_standalone_replies')) ||
		numberOfReplies <= getDefaultNumVisibleReplies(numberOfReplies, isStandaloneComment)
	) {
		return ViewReplyOptions.ALL;
	}

	return ViewReplyOptions.LIMITED;
};

export const getAdfForQuickReplyMention = (replyToCommentUser: ReplyToCommentUser | undefined) => {
	if (!replyToCommentUser || !replyToCommentUser.displayName || !replyToCommentUser.accountId) {
		return undefined;
	}

	return doc(
		p(
			mention({
				id: replyToCommentUser.accountId,
				text: `@${replyToCommentUser.displayName}`,
			}),
			text(' '),
		),
	);
};
